import AppResourceCollection from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection";
import {store} from "../index";
import PropertyAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/PropertyAppResource";
import {ACRES, SIZE_UNIT_MAPING} from "./variables";
import get from 'lodash/get';

/**
 * @param property {PropertyAppResource}
 * @returns {Boolean}
 */
export function checkIsFavorite(property) {
    if (!(property instanceof PropertyAppResource)) {
        console.error('property must be a PropertyAppResource');
        return false;
    }

    const propertiesCollection = store.getters.getProperties;
    if (
        propertiesCollection &&
        propertiesCollection.isNotEmpty
    ) {
        const isFavoriteIndex = propertiesCollection.toArray()
            .findIndex(instance =>
                instance.workspaceUuid === store.getters.getWorkspaceUuid &&
                instance.isFavorite && !!instance.property && instance.property.uuid === property.uuid);

        if (isFavoriteIndex !== -1) {
            return true;
        }
    }

    const instances = property.instances;
    if (
        instances &&
        instances instanceof AppResourceCollection &&
        instances.isNotEmpty
    ) {
        const isFavoriteIndex = instances.toArray()
            .findIndex(instance =>
                instance.workspaceUuid === store.getters.getWorkspaceUuid &&
                instance.isFavorite);
        if (isFavoriteIndex !== -1) {
            return true;
        }
    }
    return false;
}

/**
 *
 * @param lotSize
 * @returns {{unit: string, value: number}}
 */
export function lotSizeRender(lotSize = 0) {
    if (lotSize > ACRES.QUARTER) {
        lotSize /= ACRES.FULL;
        return {
            value: lotSize % 1 ? lotSize.toFixed(2) : lotSize,
            unit: SIZE_UNIT_MAPING['acres'],
        };
    }

    return {
        value: lotSize,
        unit: SIZE_UNIT_MAPING['square feet'],
    };
}

/**
 * Format the number/price
 *
 * @param value {number}
 * @param isPrice
 * @returns {string}
 */
export function formatNumber(value = 0, isPrice = true) {
    return (isPrice && (value || value === 0) ? '$' : '')
        + (value || value === 0 ? value : '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Concatenates given value to an array based on a given predicate function or int fix position
 *
 * @param array {array}
 * @param predicate {function|int}
 * @param value
 * @returns {array}
 */
export function arrayConcatAt(array, predicate, value) {
    return array.reduce(
        (c, n, idx) => {
            const at = typeof predicate === 'function' ? predicate(idx, n, array) : predicate === idx;
            const newValue = typeof value === 'function' ? value(n, idx, array) : value;
            return at ? [...c, n, newValue] : [...c, n];
        }, []);
}

/**
 * Returns parity of given number
 *
 * @param n {int}
 * @returns {Boolean}
 */
export function idOdd(n) {
    return n % 2;
}

/**
 * Returns callable function (with boolean return on usage) for checking specified array nth pos
 *
 * @param nth {int}
 * @returns {function}
 */
export function isNth(nth) {
    return n => n % nth === nth - 1;
}

/**
 * Returns increment
 *
 * @param n {int}
 * @returns {int}
 */
export function increment(n) {
    return n => ++n;
}

export function buildFilterDataObjectFromFilter(filter) {
    return {
        baths: filter.baths,
        beds: filter.beds,
        homeTypes: filter.homeType,
        sale: filter.sale,
        keywords: filter.keywords,
        location: filter.location,
        price: {
            min: (filter.price || {}).min || null,
            max: (filter.price || {}).max || null,
        },
        yearBuilt: {
            min: (filter.yearBuilt || {}).min || null,
            max: (filter.yearBuilt || {}).max || null,
        },
        squareFeet: {
            min: (filter.squareFeet || {}).min || null,
            max: (filter.squareFeet || {}).max || null,
        },
        lotSize: {
            min: (filter.lotSize || {}).min || null,
            max: (filter.lotSize || {}).max || null,
        },
        search: {
            label: filter.searchLabel,
            field: filter.search ? filter.search.nested : null,
            value: filter.search ? filter.search.value : null,
        },
        includePending: filter.includePending,
    };
}

export function fetchImageCO(url, crossOrigin = false) {
    if (typeof fetchImageCO.cache_failed == 'undefined') {
        fetchImageCO.cache_failed = {};
    }
    return new Promise((resolve, reject) => {
        if (url.indexOf('data:image/') > -1) {
            return resolve(url);
        } else if (!url || typeof fetchImageCO.cache_failed[url] !== 'undefined') {
            return reject({
                errorData: (url ? fetchImageCO.cache_failed[url] : null) || 'Missing url',
                url: url
            });
        }

        try {
            const img = new Image();
            if (crossOrigin && 'crossOrigin' in img) {
                img.crossOrigin = "Anonymous";
            }
            img.onload = () => {
                resolve(url);
            };
            img.onerror = (errorData) => {
                fetchImageCO.cache_failed[url] = errorData;
                reject({errorData: errorData, url: url});
            };

            img.src = url;
        } catch (e) {
            fetchImageCO.cache_failed[url] = e;
            reject({errorData: e, url: url});
        }
    });
}

export async function googleStreetViewImages(uIdentifier, lat, lng, maxImages = 3, radius = 50) {
    if (typeof googleStreetViewImages.cache == 'undefined') {
        googleStreetViewImages.cache = {};
    }

    if (!lat || !lng || !window.google || typeof googleStreetViewImages.cache[uIdentifier] !== 'undefined') {
        return new Promise(function (resolve) {
            resolve(googleStreetViewImages.cache[uIdentifier] || []);
        });
    } else {
        googleStreetViewImages.cache[uIdentifier] = [];
    }

    return (new window.google.maps.StreetViewService())
        .getPanorama({
            location: {
                lat: lat,
                lng: lng
            },
            preference: window.google.maps.StreetViewPreference.NEAREST,
            radius: radius
        }).then(({data}) => {
            if (!!((data || {}).location || {}).pano) {

                //if we have a street view image pano identifier we can request our image
                let found = {};

                let mainPanoId = data.location.pano;

                let getCoord = (panoId) => {
                    let foundPano = {};
                    for (let key of Object.keys(data)) {
                        if (!foundPano.lat && !foundPano.lng) {
                            foundPano = get(data, `${key}.${panoId}`, {
                                lat: null,
                                lng: null
                            });
                        }
                    }
                    if (!foundPano.lat && !foundPano.lng) {
                        return '';
                    }

                    return [foundPano.lat, foundPano.lng].join(',')
                        .trim()
                        .replace(/^,+|,+$/g, '')
                        .trim();
                };


                let urlStreetView = ['local', 'development'].includes(window.env.APP_ENV)
                    ? 'https://maps.google.com/cbk?output=thumbnail&panoid=%panoId%'
                    // eslint-disable-next-line max-len
                    : `https://maps.googleapis.com/maps/api/streetview?location=%coord%&heading=%heading%&key=${window.env.GOOGLE_API_KEY}`;

                let replace = {
                    panoId: mainPanoId,
                    heading: (data.tiles || {}).originHeading || (data.links[0] || {}).heading || 0,
                    coord: getCoord(mainPanoId),
                };


                if (mainPanoId && replace.coord.indexOf(',') > -1) {
                    found[mainPanoId] = urlStreetView;
                    Object.keys(replace).forEach(
                        key => found[mainPanoId] = found[mainPanoId].replaceAll(`%${key}%`, replace[key])
                    );
                }

                for (const i in data.links) {
                    replace.panoId = data.links[i].pano;
                    replace.heading = (data.links[i] || {}).heading || 0;
                    replace.coord = getCoord(replace.panoId);

                    if (replace.panoId && replace.panoId !== mainPanoId && replace.coord.indexOf(',') > -1) {
                        found[replace.panoId] = urlStreetView;
                        Object.keys(replace).forEach(
                            key => found[replace.panoId] = found[replace.panoId].replaceAll(`%${key}%`, replace[key])
                        );

                        if (Object.keys(found).length >= maxImages) {
                            break;
                        }
                    }
                }

                googleStreetViewImages.cache[uIdentifier] = Object.keys(found).map(key => found[key]);
            }
            return googleStreetViewImages.cache[uIdentifier];
        }).catch(e => {
            return googleStreetViewImages.cache[uIdentifier];
        });
}

export async function fetchImageWithFallback(imgSrc, propertyUuid = null, lat = null, lng = null, maxAltImages = 3) {
    let fillerPhoto = require('./../assets/images/property_default.jpg'),
        srcNotFiller = function (data) {
            return data.indexOf('data:image/') !== -1 || data !== fillerPhoto &&
                data.substr(data.lastIndexOf('/') + 1) !== fillerPhoto.substr(fillerPhoto.lastIndexOf('/') + 1);
        };

    return new Promise((resolve, reject) => {
        try {
            if (imgSrc.indexOf('maps.google') > -1) {
                return resolve([imgSrc]);
            }
            fetchImageCO(imgSrc, true)
                .then(url => {
                    resolve([url]);
                })
                .catch(async ({errorData, url}) => {
                    if (srcNotFiller(url)) {
                        let gImages = propertyUuid && lat && lng
                            ? await googleStreetViewImages(propertyUuid, lat, lng, maxAltImages)
                            : [];

                        if (gImages.length > 0) {
                            resolve(gImages);
                        }
                    }

                    reject([fillerPhoto]);
                });
        } catch (e) {
            reject([fillerPhoto]);
        }
    });
}

/**
 * @param imageUrl
 */
export function checkImageFromGoogle(imageUrl) {
    return imageUrl && imageUrl.indexOf('maps.google') > -1
        ? (imageUrl.indexOf('com/cbk') > -1 ? 1 : 2)
        : 0;
}

/**
 * @param imageUrl
 * @param maxWidth
 * @param maxHeight
 */
export function addResizeImageDataToUrl(imageUrl, maxWidth = null, maxHeight = null) {
    if (Boolean(imageUrl) && maxWidth !== false && maxHeight !== false) {
        let suffix, glue = imageUrl.indexOf('?') > -1 ? '&' : '?';

        switch (checkImageFromGoogle(imageUrl)) {
            case 1:
                suffix = `w=${maxWidth}&h=${maxHeight}`;
                break;
            case 2:
                suffix = `size=${maxWidth}x${maxHeight}`;
                break;
            default:
                suffix = `width=${maxWidth}&height=${maxHeight}`;
                break;
        }

        return imageUrl.indexOf(suffix) > -1
            ? imageUrl
            : (imageUrl + glue + suffix);
    }

    return imageUrl;
}
